import React, { useEffect } from 'react';

import { wktToGeoJSON } from '@terraformer/wkt';
import { AttentionCircleIcon, InfoCircleFilledIcon } from '@vlabs/icons';
import { Control, SettingsItemWrapper, Tooltip } from '@vlabs/uikit';
import i18n from 'i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation, Trans } from 'react-i18next';

import st from './DroiForm.module.sass';

function validateDroiWkt(droi) {
  const str = (droi.area || '').trim();
  if (!str) return true;

  try {
    wktToGeoJSON(str);
    return true;
  } catch (e) {
    return i18n.t('videoAnalytics:form.DROI.error');
  }
}

function DroiTextarea({ name, onChange, errorMessage, value }) {
  const { t } = useTranslation();
  const droi = value || { mode: 'abs', form: 'wkt', area: '' };

  useEffect(() => {
    if (!value || value.form !== 'wkt' || !value.mode || value.area === undefined) {
      onChange({
        mode: 'abs',
        form: 'wkt',
        area: value?.area || '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChangeArea = (area) => onChange({ ...droi, form: 'wkt', area });

  return (
    <Control.Textarea
      className={st.Textarea}
      errorMessage={errorMessage}
      hasError={!!errorMessage}
      name={`${name}.area`}
      onChangeValue={onChangeArea}
      placeholder={t('videoAnalytics:form.DROI.placeholder')}
      value={droi.area || ''}
    />
  );
}

const DroiTitleWithHint = () => {
  const { t } = useTranslation();
  const modalContent = (
    <div className={st.DroiHint}>
      <div className={st.DroiHint__Title}>
        <InfoCircleFilledIcon />
        <h5>{t('videoAnalytics:form.DROI.tooltip.title')}</h5>
      </div>
      <div>
        <div className={st.DroiHint__Body}>
          <Trans
            components={{ highlight: <span className={st.DroiHint__Code} /> }}
            i18nKey="videoAnalytics:form.DROI.tooltip.modal content"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className={st.DroiSettingsItem__Title}>
      <span>{t('videoAnalytics:form.DROI.label')}</span>
      <Tooltip
        modalContent={modalContent}
        tooltipContent={<Trans i18nKey="videoAnalytics:form.DROI.tooltip.preview" />}
        tooltipProps={{ place: 'right' }}
      >
        <AttentionCircleIcon className={st.TooltipIcon} />
      </Tooltip>
    </div>
  );
};

export function DroiForm({ name }) {
  const { control } = useFormContext();
  return (
    <SettingsItemWrapper
      className={st.SettingItem}
      contentClassName={st.SettingItem__Content}
      node={<DroiTitleWithHint />}
      titleClassName={st.SettingItem__Title}
    >
      <Controller
        control={control}
        name={name}
        render={({ field: { value, onChange }, fieldState }) => (
          <DroiTextarea
            errorMessage={fieldState.error?.message}
            name={name}
            onChange={onChange}
            value={value}
          />
        )}
        rules={{ validate: validateDroiWkt }}
      />
    </SettingsItemWrapper>
  );
}
